import React from 'react';
import {
  CategoriesCell,
  PesticideGroupsCell,
  ChemicalFamiliesCell,
  RiskSymbolCell,
  ActionLinkCell,
  TagsCell,
  SimpleTextCell,
} from '@client/components/ui/table/TableCells';
import TruncatedText from '@client/components/ui/TruncatedText';
import { formatApprovalNumber } from '@client/utils/helpers';

import { BiologicalLegend } from '@client/components/ui/productLegends';

/**
 * Factory function for expanded row rendering
 *
 * Creates a render function that displays additional record information
 * in an expandable row format using organized tag groups.
 *
 * @returns {Function} Expanded row render function
 *
 * @example
 * const expandedRowRender = createExpandedRowRender();
 * <Table expandable={{ expandedRowRender }} />
 */
// eslint-disable-next-line react/display-name
export const createExpandedRowRender = () => (record) => {
  // eslint-disable-next-line no-undef
  const uniqueItems = new Map();

  const addUnique = (id, name) => {
    if (name && !uniqueItems.has(id)) {
      uniqueItems.set(id, name);
    }
  };

  record.activity_sectors?.forEach((sector) => {
    addUnique(sector.id, sector.name);
  });

  record.cultures?.forEach((culture) => {
    addUnique(culture.id, culture.french_name);
  });

  const tagGroups = Array.from(uniqueItems.entries())
    .slice(0, 20)
    .map(([id, name]) => ({
      key: id,
      name: name,
    }));

  return (
    <div className="expanded-row">
      <TagsCell
        items={tagGroups}
        getItemName={(item) => item.name}
        getItemKey={(item) => item.key}
      />
    </div>
  );
};

/**
 * Complete table columns configuration for active substances
 *
 * Defines all columns including their rendering, sorting behavior,
 * and display properties. Supports both server-side and client-side sorting.
 *
 * @param {Function} t - Internationalization function
 * @param {Object} currentSorting - Current sorting state
 * @param {string} currentSorting.field - Currently sorted field
 * @param {string} currentSorting.order - Current sort order ('ascend'|'descend')
 * @returns {Array} Complete Antd Table columns configuration
 *
 * @example
 * const columns = createTableColumns(t, { field: 'name', order: 'ascend' });
 * <Table columns={columns} />
 */
export const createTableColumns = (t, currentSorting) => [
  {
    title: t('product|table.Product name (Product type)'),
    dataIndex: 'name',
    key: 'name',
    render: (text, record) => (
      <div className="flex flex-col gap-2 justify-start items-start">
        <a
          href={`/produits-commerciaux/details/${record.id}`}
          target="_blank"
          rel="noreferrer"
          className="text-[#105235] hover:text-[#105235] !font-[700] underline text-[12px]"
        >
          <TruncatedText text={text} maxLength={35} />
        </a>
        {record?.is_organic_approval && <BiologicalLegend />}
      </div>
    ),
    sorter: true,
    sortOrder: currentSorting?.field === 'name' ? currentSorting.order : null,
    width: 60,
  },
  {
    title: t('product|Étiquette'),
    dataIndex: 'pollinator_protection_label',
    key: 'pollinator_protection_label',
    render: (pollinator_protection_label) => (
      <SimpleTextCell text={pollinator_protection_label?.name} />
    ),
    sorter: true,
    sortOrder:
      currentSorting?.field === 'pollinator_protection_label'
        ? currentSorting.order
        : null,
    width:60,
  },
  {
    title: t('product|table.Homologation number'),
    dataIndex: 'approval_number',
    key: 'approval_number',
    render: (approval_number) => (
      <TruncatedText text={formatApprovalNumber(approval_number)} maxLength={40} />
    ),
    sorter: true,
    sortOrder:
      currentSorting?.field === 'approval_number' ? currentSorting.order : null,
    width: 60,
  },
  {
    title: t('product|table.Commercial packagings'),
    dataIndex: 'packagings',
    key: 'packagings.name',
    render: (packagings) => <CategoriesCell categories={packagings} />,
    sorter: true,
    sortOrder:
      currentSorting?.field === 'packagings.name' ? currentSorting.order : null,
    width: 60,
  },
  {
    title: t('product|table.Canadian distributor'),
    key: 'canadian_distributor.title',
    dataIndex: 'canadian_distributor',
    render: (canadian_distributor) => {
      return (
        <TruncatedText text={canadian_distributor?.title} maxLength={40} />
      );
    },
    sorter: { compare: () => 0 },
    sortDirections: ['ascend', 'descend'],
    sortOrder:
      currentSorting?.field === 'canadian_distributor.title'
        ? currentSorting.order
        : null,
    width: 60,
  },
  {
    title: t('product|table.Active substance (Pesticide groups)'),
    dataIndex: 'active_substances',
    key: 'active_substances.active_substance.name',
    render: (active_substances, record) => (
      <>
        {active_substances && active_substances.length > 1 && (
          <div className="flex flex-col gap-4">
            <CategoriesCell
              categories={active_substances?.map((a) => a?.active_substance)}
            />
            <PesticideGroupsCell
              associations={record?.pesticide_group_associations}
            />
          </div>
        )}
      </>
    ),
    sorter: { compare: () => 0 },
    sortDirections: ['ascend', 'descend'],
    sortOrder:
      currentSorting?.field === 'active_substances.active_substance.name'
        ? currentSorting.order
        : null,
    width: 60,
  },
  {
    title: t('product|table.Chemical family'),
    dataIndex: 'pesticide_group_associations',
    key: 'pesticide_group_associations.chemical_family.label',
    render: (associations) => (
      <ChemicalFamiliesCell associations={associations} />
    ),
    sorter: { compare: () => 0 },
    sortDirections: ['ascend', 'descend'],
    sortOrder:
      currentSorting?.field === 'pesticide_group_associations.chemical_family.label'
        ? currentSorting.order
        : null,
    width:60,
  },
  {
    title: t('product|table.Activity type'),
    dataIndex: 'activity_type_associations',
    key: 'activity_type_associations.activity_type.label',
    render: (associations) => (
      <CategoriesCell
        categories={associations?.map((a) => a.activity_type).filter(Boolean)}
        label="label"
      />
    ),
    sorter: { compare: () => 0 },
    sortDirections: ['ascend', 'descend'],
    sortOrder:
      currentSorting?.field === 'activity_type_associations.activity_type.label'
        ? currentSorting.order
        : null,
    width: 60,
  },

  {
    title: t('active-substance|table.Risks on health'),
    children: [
      {
        title: t('active-substance|table.Mammals (including humans)'),
        children: [
          {
            title: t('active-substance|table.Toxicity aigue'),
            key: 'acute_toxicity',
            className: 'vertical-cell',
            render: (_, record) => (
              <RiskSymbolCell
                symbolData={record.symbols?.health?.acute_toxicity_symbol}
                alt="Acute toxicity"
              />
            ),
            width: 40,
          },
          {
            title: t('active-substance|table.Long term effects'),
            key: 'long_term_effects',
            className: 'vertical-cell',
            render: (_, record) => (
              <RiskSymbolCell
                symbolData={record.symbols?.health?.long_term_effect_symbol}
                alt="Long term effects"
              />
            ),
            width: 40,
          },
        ],
      },
    ],
    width: 100,
  },
  {
    title: t('active-substance|table.Risks on environment'),
    children: [
      {
        title: t('active-substance|table.Non target species'),
        children: [
          {
            title: t('active-substance|table.Fish or dophani'),
            key: 'fish_daphnia',
            className: 'vertical-cell',
            render: (_, record) => (
              <RiskSymbolCell
                symbolData={record.symbols?.environment?.fishes_symbol}
                alt="Fish or daphnia"
              />
            ),
            width: 40,
          },
          {
            title: t('active-substance|table.Birds'),
            key: 'birds',
            className: 'vertical-cell',
            render: (_, record) => (
              <RiskSymbolCell
                symbolData={record.symbols?.environment?.birds_symbol}
                alt="Birds"
              />
            ),
            width: 40,
          },
          {
            title: t('active-substance|table.Bees'),
            key: 'bees',
            className: 'vertical-cell',
            render: (_, record) => (
              <RiskSymbolCell
                symbolData={record.symbols?.environment?.bees_symbol}
                alt="Bees"
              />
            ),
            width: 40,
          },
        ],
      },
      {
        title: t('active-substance|table.Fate and behaviour'),
        children: [
          {
            title: t('active-substance|table.Persistance'),
            key: 'persistence',
            className: 'vertical-cell',
            render: (_, record) => (
              <RiskSymbolCell
                symbolData={record.symbols?.environment?.persistence_symbol}
                alt="Persistence"
              />
            ),
            width: 40,
          },
          {
            title: t('active-substance|table.Leaching potential'),
            key: 'leaching_potential',
            className: 'vertical-cell',
            render: (_, record) => (
              <RiskSymbolCell
                symbolData={record.symbols?.environment?.leaching_symbol}
                alt="Leaching potential"
              />
            ),
            width: 40,
          },
        ],
      },
    ],
    width: 200,
  },
  {
    title: t('product|table.Product file'),
    key: 'details',
    className: 'vertical-cell',
    render: (_, record) => (
      <ActionLinkCell
        href={`/produits-commerciaux/details/${record.id}`}
        target="_blank"
        className="btn btn--primary btn--sm"
      >
        {t('active-substance|table.See')}
      </ActionLinkCell>
    ),
    width: 40,
  },
];